import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: ReactNode;
  placement?: TooltipPlacement;
  delay?: number;
  children: ReactElement<{
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    'aria-describedby'?: string;
  }>;
}

interface Position {
  top: number;
  left: number;
}

const placementClasses: Record<TooltipPlacement, string> = {
  top: '-translate-x-1/2 -translate-y-full -mt-1',
  bottom: '-translate-x-1/2 mt-1',
  left: '-translate-x-full -translate-y-1/2 -ml-1',
  right: 'translate-x-0 -translate-y-1/2 ml-1',
};

function computePosition(rect: DOMRect, placement: TooltipPlacement): Position {
  switch (placement) {
    case 'top':
      return { top: rect.top + window.scrollY, left: rect.left + window.scrollX + rect.width / 2 };
    case 'bottom':
      return { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX + rect.width / 2 };
    case 'left':
      return { top: rect.top + window.scrollY + rect.height / 2, left: rect.left + window.scrollX };
    case 'right':
      return { top: rect.top + window.scrollY + rect.height / 2, left: rect.right + window.scrollX };
  }
}

export function Tooltip({ content, placement = 'top', delay = 200, children }: TooltipProps) {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition(computePosition(rect, placement));
        setVisible(true);
      }
    }, delay);
  }, [delay, placement]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!isValidElement(children)) return children;

  const childProps = children.props;

  const trigger = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
    },
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      childProps.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      childProps.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      childProps.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      childProps.onBlur?.(e);
    },
    'aria-describedby': visible ? tooltipId : childProps['aria-describedby'],
  } as Record<string, unknown>);

  return (
    <>
      {trigger}
      {visible && position
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              style={{ position: 'absolute', top: position.top, left: position.left }}
              className={`z-50 pointer-events-none px-2 py-1 rounded-md text-xs font-medium bg-surface-900 text-white shadow-lg max-w-xs whitespace-normal ${placementClasses[placement]}`}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
